<script setup lang="ts">
import { signOut } from "firebase/auth";
import { DateTime } from "luxon";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCurrentUser } from "vuefire";
import { useLoading } from "../composables/useLoading";
import { useNotification } from "../composables/useNotification";
import { UserStore, useStores } from "../composables/useStores";
import { AccountingService } from "../services/accounting";
import { auth } from "../services/firebase";
import { FirestoreService } from "../services/firestore";
import {
  AccountingCalculations,
  DailyAccounting,
} from "../types/daily-accounting";
import { Cents } from "../types/money";
import { PaymentSummary } from "../types/payment-summary";
import { Store } from "../types/store";
import { getStoreName, getUserMapping } from "../utils/user-mapping";
const { setText, toggleLoading } = useLoading();
const { setError } = useNotification();
const { setStores, setCurrentStore } = useStores();
// const currentYear = ref(DateTime.now().toFormat("yyyy"));
const currentMonth = ref(DateTime.now().toFormat("MM"));
const router = useRouter();
const route = useRoute();
const logout = async () => {
  setText("logging out...");
  toggleLoading(true);
  try {
    await signOut(auth);
    toggleLoading(false);
    await router.push({ name: "LoginPage" });
  } catch (e) {
    console.log(e);
  } finally {
    toggleLoading(false);
  }
};
// const currentYearMonth = computed(
//   () => `${currentYear.value}-${currentMonth.value}`,
// );

// Seed the date from the URL so a refresh keeps the chosen date (a fresh visit
// with no ?date= still starts at today), and mirror changes back to the URL.
const isValidDate = (s: unknown): s is string =>
  typeof s === "string" && DateTime.fromFormat(s, "yyyy-MM-dd").isValid;
const selectedDate = ref(
  isValidDate(route.query.date)
    ? route.query.date
    : DateTime.now().toFormat("yyyy-MM-dd"),
);
watch(selectedDate, (date) => {
  if (route.query.date !== date) {
    router.replace({ query: { ...route.query, date } });
  }
});
const user = useCurrentUser();
const years = ref<string[]>([]);
const months = ref<string[]>([]);
const sitePayments = ref<any[]>([]);
const getExtraUserStores = async (userId: string) => {
  const result: UserStore[] = [];
  const mappings = getUserMapping(userId);
  console.log({ userId });
  console.log({ mappings });
  for (const mapping of mappings) {
    const store = await FirestoreService.getDocument<Store>(mapping.path);
    if (store) {
      console.log({
        user: mapping.user,
        merchant: mapping.merchant,
        store: mapping.store,
        data: store,
      });
      result.push({
        user: mapping.user,
        merchant: mapping.merchant,
        store: mapping.store,
        data: store,
      });
    }
  }
  return result;
};
/** Disputes entered in the Atas Pay Accounting app (atas-pay-acc). */
const dailyAccounting = ref<DailyAccounting>();

/**
 * Rejects if `promise` hasn't settled within `ms`, so a Firestore read that
 * stalls (flaky connectivity) can never leave the loading overlay stuck on.
 */
const withTimeout = <T>(promise: Promise<T>, ms: number, label: string) => {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() =>
    clearTimeout(timer),
  ) as Promise<T>;
};
const READ_TIMEOUT_MS = 15000;

/**
 * Each date change starts a new load. Switching dates quickly overlaps them, so
 * every run tags itself and bails the moment a newer one starts — only the
 * latest run writes results or clears the spinner, which stops a stale run from
 * showing the wrong date's data or turning the overlay off underneath the newer
 * load.
 */
let latestLoad = 0;

/** Bonus disputes figure; short timeout since it must never delay the payments. */
const ACCOUNTING_TIMEOUT_MS = 5000;

const refresh = async () => {
  // Wait for auth: reading unauthenticated just gets denied/stalls, and the
  // watcher re-runs the moment `user` resolves.
  if (!user.value) return;
  const loadId = ++latestLoad;
  const isStale = () => loadId !== latestLoad;
  setText("Loading payments...");
  toggleLoading(true);
  try {
    sitePayments.value = [];
    dailyAccounting.value = undefined;

    // Essential path first — the stores and the day's payment summaries.
    const ss = await withTimeout(
      getExtraUserStores(user.value.uid),
      READ_TIMEOUT_MS,
      "stores",
    );
    if (isStale()) return;
    if (ss.length == 0) {
      setError("No stores found for the user");
      return;
    }
    setStores(ss);
    setCurrentStore(ss[0]);
    let results: any[] = [];
    for (const store of ss) {
      const path = `/users/${store.user}/merchants/${store.merchant}/stores/${
        store.store
      }/months/${DateTime.fromFormat(selectedDate.value, "yyyy-MM-dd").toFormat(
        "yyyy-MM",
      )}/payments-summary/payments-daily-summary.${store.store}#${
        selectedDate.value
      }`;

      const result = await withTimeout(
        FirestoreService.getDocument<PaymentSummary>(path),
        READ_TIMEOUT_MS,
        "payments",
      );
      if (isStale()) return;
      if (result) {
        results = [
          ...results.filter((x) => x.id !== store.store),
          {
            id: store.store,
            merchant: store.merchant,
            user: store.user,
            site: getStoreName(store.store),
            payments: result,
          },
        ];
      }
    }
    sitePayments.value = results;

    // Disputes are a bonus figure — load them without blocking or failing the
    // payments view. A missing, denied, or slow accounting doc just leaves the
    // net-settlement figures as "Pending" and never surfaces an error.
    const date = selectedDate.value;
    withTimeout(AccountingService.get(date), ACCOUNTING_TIMEOUT_MS, "accounting")
      .then((d) => {
        if (loadId === latestLoad) dailyAccounting.value = d;
      })
      .catch((e) => console.error("failed to load daily accounting", e));
  } catch (e) {
    if (!isStale()) setError("failed to load payments please try again");
  } finally {
    // Only the latest run owns the spinner; a superseded run leaves it on for
    // the newer load to clear.
    if (!isStale()) toggleLoading(false);
  }
};
watch(
  [selectedDate, user],
  async () => {
    await refresh();
  },
  { immediate: true },
);
const days = ["01", "02", "03", "04", "05", "06", "07", "08", "09"];
for (let i = 10; i <= 31; i++) {
  days.push(i.toString());
}
onMounted(() => {
  years.value = Array.from({ length: 5 }, (_, i) =>
    DateTime.now().minus({ years: i }).toFormat("yyyy"),
  );
  months.value = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
});

const total = computed<Cents>(() =>
  sitePayments.value.reduce(
    (acc, summary) => acc + (summary.payments.total ?? 0),
    0,
  ),
);

const asCurrency = (cents: Cents) =>
  (cents / 100).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
// const surcharge = computed(() =>
//   paymentSummaries.value.reduce(
//     (acc, summary) => acc + PaymentSummary.surcharge(summary),
//     0
//   )
// );

/** Set in the Accounting app; absent until someone records the day's disputes. */
const disputes = computed<Cents>(
  () => dailyAccounting.value?.disputes?.amount ?? 0,
);
/**
 * Until disputes are recorded, the day's settlement is unknown — showing
 * Total - Fees - $0.00 would present an unreviewed day as a settled one.
 */
const disputesRecorded = computed(() =>
  DailyAccounting.disputesRecorded(dailyAccounting.value),
);

const fees = computed<Cents>(() => AccountingCalculations.fees(total.value));
const netSettlement = computed<Cents>(() =>
  AccountingCalculations.netSettlement(total.value, disputes.value),
);

// const refunds = computed(() =>
//   paymentSummaries.value.reduce(
//     (acc, summary) => acc + (PaymentSummary.refund(summary) ?? 0),
//     0
//   )
// );

const monthMapping = {
  January: "01",
  February: "02",
  March: "03",
  April: "04",
  May: "05",
  June: "06",
  July: "07",
  August: "08",
  September: "09",
  October: "10",
  November: "11",
  December: "12",
};
const selectedMonth = ref<keyof typeof monthMapping>(
  DateTime.now().toFormat("LLLL") as keyof typeof monthMapping,
);
watch(selectedMonth, (selectedMonth) => {
  currentMonth.value = monthMapping[selectedMonth];
});
</script>
<template>
  <div class="px-6 pb-8">
    <div class="flex flex-row justify-start items-center space-x-10">
      <h1 class="text-black font-bold text-2xl">Dashboard</h1>
      <a
        href="#"
        class="text-primary underline font-medium text-base font-poppins"
        @click.prevent="refresh"
        >Reload Payments</a
      >
      <a
        href="#"
        @click.prevent="logout"
        class="text-primary underline font-medium text-base font-poppins block"
        >Logout</a
      >
    </div>
    <div class="mt-8 mb-6">
      <input v-model="selectedDate" type="date" class="border rounded p-2" />
    </div>

    <!-- Summary figures -->
    <dl class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      <div class="rounded-[16px] bg-white p-6 shadow-sm">
        <dt class="text-sm font-medium text-gray-500">Total processed</dt>
        <dd class="mt-2 text-3xl font-semibold font-poppins text-gray-900">
          {{ asCurrency(total) }}
        </dd>
        <dd class="mt-1 text-xs text-gray-400">
          {{ sitePayments.length }} site(s)
        </dd>
      </div>

      <div class="rounded-[16px] bg-white p-6 shadow-sm">
        <dt class="text-sm font-medium text-gray-500">Fees (10%)</dt>
        <dd class="mt-2 text-3xl font-semibold font-poppins text-gray-900">
          {{ asCurrency(fees) }}
        </dd>
      </div>

      <div class="rounded-[16px] bg-white p-6 shadow-sm">
        <dt class="text-sm font-medium text-gray-500">Disputes</dt>
        <dd
          v-if="disputesRecorded"
          class="mt-2 text-3xl font-semibold font-poppins text-gray-900"
        >
          {{ asCurrency(disputes) }}
        </dd>
        <dd v-else class="mt-3 text-lg font-medium text-gray-400">
          Not recorded
        </dd>
      </div>

      <div class="rounded-[16px] bg-white p-6 shadow-sm">
        <dt class="text-sm font-medium text-gray-500">Net settlement</dt>
        <dd
          v-if="disputesRecorded"
          class="mt-2 text-3xl font-semibold font-poppins text-gray-900"
        >
          {{ asCurrency(netSettlement) }}
        </dd>
        <dd v-else class="mt-3 text-lg font-medium text-gray-400">Pending</dd>
      </div>
    </dl>

    <!-- Per-site breakdown -->
    <div class="rounded-[16px] bg-white p-6 max-w-3xl">
      <h2 class="text-xl font-semibold mb-4">Sites</h2>
      <p
        v-if="!sitePayments || sitePayments.length === 0"
        class="text-gray-500"
      >
        No payments for this date.
      </p>
      <table v-else class="min-w-full">
        <thead>
          <tr class="text-left text-xs font-medium text-gray-500">
            <th scope="col" class="pb-3 pr-10">Site</th>
            <th scope="col" class="pb-3 text-right">Total</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-for="payment in sitePayments" :key="payment.site">
            <td class="py-4 pr-10">
              <router-link
                :to="{
                  name: 'store-payments',
                  params: {
                    date: payment.payments.date,
                    store: payment.id,
                    merchant: payment.merchant,
                    user: payment.user,
                  },
                }"
                class="text-primary hover:underline font-medium"
              >
                {{ payment.site
                }}<span class="sr-only">, {{ payment.payments.id }}</span>
              </router-link>
            </td>
            <td class="py-4 text-right font-mono text-gray-700">
              {{ asCurrency(payment.payments.total ?? 0) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
